
/*!
 * Copyright (C) 2022-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 */
/*
 *		2022-08-16
 *			- Refactor filter-widget.js hotel room icon related functionality to <hotel-room-icons> component.
 *
 *		2024-06-10 (jhoward)
 *			- Implement Typescript in <hotel-room-icons>.
 *			- Refactor <hotel-room-icons> to use BEM CSS naming.
 *			- Implement low inventory notification in <hotel-room-icons>.
 *			- Make ADA variants clickable in <hotel-room-icons> for easier booking.
*/
import {defineComponent, PropType} from 'vue'
import {HotelPage, HotelRoomData, RoomAvailabilityData} from "./defs";
import {mapGetters} from "vuex";

export default defineComponent({
  name: 'hotelRoomIcons',
  props: {
    roomAvailabilityData: {
      type: Object as PropType<RoomAvailabilityData>,
      required: true
    },
    roomData: {
      type: Array as PropType<HotelRoomData[]>,
      required: true
    },
    page: {
      type: Object as PropType<HotelPage>,
      required: true
    }
  },
  data() {
    return {
      lowInventoryThresholdCount: 10,
      lowInventoryThresholdPercent: 0.25,
    };
  },
  computed: {
    ...mapGetters([
      'bookingLink',
      'roomTypeTotalAvailable'
    ]),
    icons() {
      let output = {
        'non-smoking': false,
        smoking: false,
        ada: false
      };
      this.roomData.forEach(roomTypeData => {
        if (roomTypeData.ROOM_SMOKING) {
          output.smoking = true;
        }
        if (roomTypeData.ROOM_NON_SMOKING) {
          output['non-smoking'] = true;
        }
        if (roomTypeData.ROOM_ADA) {
          output.ada = true;
        }
      });

      return output;
    },
    iconsAvailable() {
      let output = {
        anyRoomType: this.isAvailable,
        'non-smoking': false,
        smoking: false,
        ada: false
      };
      this.roomAvailabilityData.ROOM_TYPES.forEach(roomType => {
        if (roomType.ROOM_SMOKING) {
          output.smoking = true;
        }
        if (roomType.ROOM_NON_SMOKING) {
          output['non-smoking'] = true;
        }
        if (roomType.ROOM_ADA) {
          output.ada = true;
        }
      });

      return output;
    },
    isAvailable() {
      return this.$store.getters.roomTypeIsAvailable(this.roomAvailabilityData);
    },
    adaVariants(): HotelRoomData[] {
      return this.roomData.filter(roomTypeData => roomTypeData.ROOM_ADA_VARIANT.length);
    },
    availabilityTooltip(): string {
      return (!this.iconsAvailable.anyRoomType)
        ? 'Room Sold Out'
        : (this.isLowInventory)
          ? 'Not many rooms left'
          : 'Room Available';
    },
    isLowInventory(): boolean {
      const totalInventory = this.$store.getters.roomTypeTotalAvailable(this.roomAvailabilityData);
      return totalInventory < this.lowInventoryThresholdCount;
    },
  },
  methods: {
    showAdaVariants() {
      const dialogBody = (!this.adaVariants.length)
        ? 'No accessibility options exist for this room type.'
        : `<p>Here are the accessibility options for "${this.page.TITLE}":</p><ul>` + this.adaVariants.map(this.getAdaVariantListItemHtml).join('') + `</ul>`;

      window.showOverlayDialog({
        alertMode: true,
        confirmAccept: 'CLOSE',
        confirmTitle: this.page.TITLE + ' Accessibility Options',
        confirmBody: dialogBody
      });
    },

    getAdaVariantListItemHtml(roomTypeData: HotelRoomData): string {
      if (this.isPageRoomTypeAvailable(roomTypeData.PRT_ID)) {
        return `<li><a href="${this.$store.getters.bookingLink(this.roomAvailabilityData, roomTypeData.PRT_ID, '', this.page.ID, 'accessibility-dialog')}" title="Click to book ${this.page.TITLE} ${roomTypeData.ROOM_ADA_VARIANT}">${roomTypeData.ROOM_ADA_VARIANT}</a></li>`;
      }

      return `<li>${roomTypeData.ROOM_ADA_VARIANT} (Sold Out)</li>`;
    },

    isPageRoomTypeAvailable(prtId: number) {
      for (let i = 0, l = this.roomAvailabilityData.ROOM_TYPES.length; i < l; i++) {
        if (
          this.roomAvailabilityData.ROOM_TYPES[i].hasOwnProperty('PRT_ID')
          && this.roomAvailabilityData.ROOM_TYPES[i].PRT_ID === prtId
        ) {
          return true;
        }
      }

      return false;
    }
  }
})
